/* Collection hero media starts after the page, eager bottle images and fonts settle. */
(function () {
  'use strict';
  if (window.stellarCollectionHero) return;

  var jobs = new WeakMap();
  var cleanups = new WeakMap();
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var IMAGE_TIMEOUT = 10000;
  var VIDEO_TIMEOUT = 12000;

  function windowLoaded() {
    if (document.readyState === 'complete') return Promise.resolve();
    return new Promise(function (resolve) { window.addEventListener('load', resolve, { once: true }); });
  }

  function imageSettled(img) {
    return new Promise(function (resolve) {
      function done() {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        if (img.naturalWidth && img.decode) img.decode().catch(function () {}).then(resolve);
        else resolve();
      }
      if (img.complete) { done(); return; }
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      if (img.complete) done();
    });
  }

  function paint() {
    return new Promise(function (resolve) {
      requestAnimationFrame(function () { requestAnimationFrame(resolve); });
    });
  }

  function idle() {
    return new Promise(function (resolve) {
      if ('requestIdleCallback' in window) window.requestIdleCallback(resolve, { timeout: 1000 });
      else setTimeout(resolve, 0);
    });
  }

  function mediaURL(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      var url = new URL(value, document.baseURI);
      return /^https?:$/.test(url.protocol) ? url.href : null;
    } catch (error) { return null; }
  }

  function decorate(element, className) {
    element.className = className;
    element.setAttribute('aria-hidden', 'true');
    element.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;object-fit:cover;pointer-events:none;';
    return element;
  }

  function readConfig(hero) {
    var node = hero.querySelector('[data-collection-hero-config]');
    try { return node ? JSON.parse(node.textContent) : {}; }
    catch (error) { return {}; }
  }

  function start(hero, host, config) {
    var controller = null;
    var nextIndex = 0;
    var generation = 0;
    var visible = hero.getBoundingClientRect().bottom > 0 && hero.getBoundingClientRect().top < innerHeight;
    var candidates = [
      { kind: 'image', name: 'collection-image', url: mediaURL(config.collectionImage) },
      { kind: 'video', name: 'collection-video', url: mediaURL(config.collectionVideo), poster: mediaURL(config.collectionPoster) },
      { kind: 'image', name: 'native-collection-image', url: mediaURL(config.nativeCollectionImage) },
      { kind: 'image', name: 'fallback-image', url: mediaURL(config.fallbackImage) },
      { kind: 'video', name: 'fallback-video', url: mediaURL(config.fallbackVideo), poster: mediaURL(config.fallbackPoster) }
    ].filter(function (candidate) { return candidate.url; });

    function state(value) { hero.setAttribute('data-hero-state', value); }
    function canMove() { return visible && !document.hidden && !reduced.matches; }
    function sync() { if (controller) controller.activate(canMove()); }
    function motionChanged() { sync(); }
    document.addEventListener('visibilitychange', sync);
    if (reduced.addEventListener) reduced.addEventListener('change', motionChanged);
    else reduced.addListener(motionChanged);
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        sync();
      }, { threshold: 0.01 });
      observer.observe(hero);
    }

    function image(candidate) {
      return new Promise(function (resolve, reject) {
        var img = decorate(document.createElement('img'), 'cf-hero-image');
        var finished = false;
        var timer = setTimeout(function () { fail(new Error('Image timed out')); }, IMAGE_TIMEOUT);
        img.alt = '';
        img.decoding = 'async';
        img.fetchPriority = 'low';

        function cleanup() {
          clearTimeout(timer);
          img.removeEventListener('load', loaded);
          img.removeEventListener('error', fail);
        }
        function fail(error) {
          if (finished) return;
          finished = true;
          cleanup();
          img.removeAttribute('src');
          reject(error);
        }
        function loaded() {
          if (!img.naturalWidth) { fail(new Error('Empty image')); return; }
          var decoded = img.decode ? img.decode().catch(function () {}) : Promise.resolve();
          decoded.then(function () {
            if (finished) return;
            finished = true;
            cleanup();
            resolve({
              element: img,
              activate: function () { state('ready-' + candidate.name); },
              destroy: function () { img.remove(); img.removeAttribute('src'); }
            });
          });
        }
        img.addEventListener('load', loaded, { once: true });
        img.addEventListener('error', fail, { once: true });
        img.src = candidate.url;
      });
    }

    function video(candidate, failedAfterReady) {
      return new Promise(function (resolve, reject) {
        var vid = decorate(document.createElement('video'), 'cf-hero-video');
        var ready = false;
        var dead = false;
        var playing = false;
        var playAttempt = 0;
        var timer = setTimeout(function () { fail(new Error('Video timed out')); }, VIDEO_TIMEOUT);
        vid.muted = true;
        vid.defaultMuted = true;
        vid.loop = true;
        vid.playsInline = true;
        vid.preload = 'auto';
        vid.controls = false;
        vid.setAttribute('muted', '');
        vid.setAttribute('playsinline', '');
        vid.setAttribute('tabindex', '-1');

        function dispose() {
          dead = true;
          playAttempt++;
          clearTimeout(timer);
          vid.removeEventListener('loadeddata', loaded);
          vid.removeEventListener('error', fail);
          vid.pause();
          vid.removeAttribute('src');
          vid.removeAttribute('poster');
          vid.load();
          vid.remove();
        }
        function fail(error) {
          if (dead) return;
          var hadReady = ready;
          dispose();
          if (hadReady) failedAfterReady();
          else reject(error);
        }
        function loaded() {
          if (ready || dead) return;
          ready = true;
          clearTimeout(timer);
          vid.removeEventListener('loadeddata', loaded);
          resolve({
            element: vid,
            activate: function (active) {
              if (dead) return;
              if (!active) {
                playAttempt++;
                playing = false;
                vid.pause();
                state('paused-' + candidate.name);
                return;
              }
              if (playing) return;
              playing = true;
              var attempt = ++playAttempt;
              var promise;
              try { promise = vid.play(); }
              catch (error) { fail(error); return; }
              Promise.resolve(promise).then(function () {
                if (!dead && attempt === playAttempt) state('playing-' + candidate.name);
              }).catch(function (error) {
                // An offscreen/reduced-motion pause can interrupt a pending play legitimately.
                if (!dead && attempt === playAttempt && canMove()) fail(error);
              });
            },
            destroy: dispose
          });
        }
        vid.addEventListener('loadeddata', loaded);
        vid.addEventListener('error', fail);
        if (candidate.poster) vid.poster = candidate.poster;
        vid.src = candidate.url;
        vid.load();
      });
    }

    function stars() {
      // The existing stellar-savor.js constellation: pale points and gradient shooting stars.
      // A unique hook prevents the original savor/shop auto-initializers sharing this canvas.
      var canvas = decorate(document.createElement('canvas'), 'cf-hero-stars');
      canvas.setAttribute('data-stellar-hero-stars', '');
      var ctx = canvas.getContext('2d');
      var width = 1, height = 1, dots = [], shots = [], last = 0, frame = 0;
      var moving = false, dead = false;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var resize;

      function draw(now, animate) {
        if (!ctx) return;
        ctx.clearRect(0, 0, width, height);
        dots.forEach(function (dot) {
          if (animate) dot.a = Math.min(1, Math.max(0.15, dot.a + (Math.random() - 0.5) * 0.05));
          ctx.globalAlpha = dot.a * 0.5;
          ctx.fillStyle = '#F1EAC9';
          ctx.beginPath(); ctx.arc(dot.x, dot.y, dot.r, 0, 7); ctx.fill();
        });
        if (animate && now - last > 3000 + Math.random() * 4000) {
          last = now;
          shots.push({ x: Math.random() * width * 0.8, y: Math.random() * height * 0.4, vx: (6 + Math.random() * 4) * dpr, vy: (2.5 + Math.random() * 2) * dpr, l: 1 });
        }
        shots = shots.filter(function (shot) { return animate && shot.l > 0; });
        shots.forEach(function (shot) {
          ctx.globalAlpha = shot.l * 0.9;
          var gradient = ctx.createLinearGradient(shot.x, shot.y, shot.x - shot.vx * 10, shot.y - shot.vy * 10);
          gradient.addColorStop(0, '#F1EAC9'); gradient.addColorStop(1, 'rgba(241,234,201,0)');
          ctx.strokeStyle = gradient; ctx.lineWidth = 1.2 * dpr;
          ctx.beginPath(); ctx.moveTo(shot.x, shot.y); ctx.lineTo(shot.x - shot.vx * 10, shot.y - shot.vy * 10); ctx.stroke();
          shot.x += shot.vx; shot.y += shot.vy; shot.l -= 0.02;
        });
        ctx.globalAlpha = 1;
      }
      function size() {
        var rect = host.getBoundingClientRect();
        width = canvas.width = Math.max(1, Math.round(rect.width * dpr));
        height = canvas.height = Math.max(1, Math.round(rect.height * dpr));
        dots = [];
        for (var i = 0; i < 60; i++) dots.push({ x: Math.random() * width, y: Math.random() * height, r: (Math.random() * 0.9 + 0.3) * dpr, a: Math.random() });
        draw(performance.now(), false);
      }
      function tick(now) {
        frame = 0;
        if (dead || !moving || !canvas.isConnected) return;
        draw(now, true);
        frame = requestAnimationFrame(tick);
      }
      size();
      if ('ResizeObserver' in window) { resize = new ResizeObserver(size); resize.observe(host); }
      else window.addEventListener('resize', size);
      return {
        element: canvas,
        activate: function (active) {
          moving = !!active && !!ctx;
          if (!moving) {
            cancelAnimationFrame(frame); frame = 0;
            draw(performance.now(), false);
            state(reduced.matches ? 'stars-static' : 'stars-paused');
          } else {
            state('stars');
            if (!frame) { last = performance.now(); frame = requestAnimationFrame(tick); }
          }
        },
        destroy: function () {
          dead = true; cancelAnimationFrame(frame);
          if (resize) resize.disconnect(); else window.removeEventListener('resize', size);
          canvas.remove();
        }
      };
    }

    async function next() {
      var token = ++generation;
      if (controller) { controller.destroy(); controller = null; }
      while (nextIndex < candidates.length) {
        var candidate = candidates[nextIndex++];
        // Posters are used only for reduced-motion video; they are never primary images.
        if (candidate.kind === 'video' && reduced.matches) {
          if (!candidate.poster) continue;
          candidate = { kind: 'image', name: candidate.name + '-poster', url: candidate.poster };
        }
        state('loading-' + candidate.name);
        try {
          var media = candidate.kind === 'image' ? await image(candidate) : await video(candidate, function () {
            if (token === generation) next();
          });
          if (token !== generation) { media.destroy(); return; }
          host.replaceChildren(media.element);
          controller = media;
          sync();
          return;
        } catch (error) {
          if (token !== generation) return;
        }
      }
      controller = stars();
      host.replaceChildren(controller.element);
      sync();
    }

    cleanups.set(hero, function () {
      generation++;
      if (controller) { controller.destroy(); controller = null; }
      if (observer) observer.disconnect();
      document.removeEventListener('visibilitychange', sync);
      if (reduced.removeEventListener) reduced.removeEventListener('change', motionChanged);
      else reduced.removeListener(motionChanged);
    });
    return next();
  }

  function scheduleHero(hero) {
    if (jobs.has(hero)) return jobs.get(hero);
    var host = hero.querySelector('.cf-hero-media');
    if (!host) return Promise.resolve(false);
    var config = readConfig(hero);
    var firstImages = Array.prototype.slice.call(document.querySelectorAll('#bottles img[loading="eager"]'));
    hero.setAttribute('data-hero-state', 'waiting-for-page');
    var scheduled = Promise.all([
      windowLoaded(),
      Promise.all(firstImages.map(imageSettled)),
      document.fonts && document.fonts.ready ? document.fonts.ready.catch(function () {}) : Promise.resolve()
    ]).then(async function () {
      // A stylesheet completing during window.load can start a newer font-ready cycle.
      if (document.fonts && document.fonts.ready) await document.fonts.ready.catch(function () {});
      hero.setAttribute('data-hero-state', 'waiting-for-idle');
      await paint();
      await idle();
      if (!hero.isConnected) return false;
      if (window.performance && performance.mark) performance.mark('cf-hero-media-start');
      return start(hero, host, config);
    });
    jobs.set(hero, scheduled);
    return scheduled;
  }

  function heroes(root) {
    root = root || document;
    return root.matches && root.matches('.cf-collection-hero') ? [root] : Array.prototype.slice.call(root.querySelectorAll('.cf-collection-hero'));
  }
  function schedule(root) {
    return Promise.all(heroes(root).map(scheduleHero));
  }
  window.stellarCollectionHero = { schedule: schedule };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { schedule(); }, { once: true });
  else schedule();
  document.addEventListener('shopify:section:load', function (event) { schedule(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    heroes(event.target).forEach(function (hero) {
      if (cleanups.has(hero)) { cleanups.get(hero)(); cleanups.delete(hero); }
      jobs.delete(hero);
    });
  });
})();
